import { create } from 'zustand';
import type { RevoteConfirmBonus, RevoteConfirmKind } from '@/constants/revote';

type RevoteConfirmResult = {
  confirmed: boolean;
  dontShow: boolean;
};

type RevoteConfirmRequest = {
  kind: RevoteConfirmKind;
  bonus?: RevoteConfirmBonus | null;
};

interface RevoteConfirmModalStore {
  open: boolean;
  kind: RevoteConfirmKind | null;
  bonus: RevoteConfirmBonus | null;
  dontShow: boolean;
  resolver: ((result: RevoteConfirmResult) => void) | null;
  openTimer: number | null;
  request: (input: RevoteConfirmKind | RevoteConfirmRequest) => Promise<RevoteConfirmResult>;
  setDontShow: (dontShow: boolean) => void;
  confirm: () => void;
  cancel: () => void;
}

function resolveAndReset(
  resolver: ((result: RevoteConfirmResult) => void) | null,
  result: RevoteConfirmResult,
  set: (partial: Partial<RevoteConfirmModalStore>) => void,
  openTimer: number | null,
): void {
  if (openTimer != null) {
    clearTimeout(openTimer);
  }
  resolver?.(result);
  set({
    open: false,
    kind: null,
    bonus: null,
    dontShow: false,
    resolver: null,
    openTimer: null,
  });
}

function normalizeRequest(input: RevoteConfirmKind | RevoteConfirmRequest): RevoteConfirmRequest {
  if (typeof input === 'string') {
    return { kind: input, bonus: null };
  }
  return input;
}

export const useRevoteConfirmModalStore = create<RevoteConfirmModalStore>((set, get) => ({
  open: false,
  kind: null,
  bonus: null,
  dontShow: false,
  resolver: null,
  openTimer: null,
  request: input =>
    new Promise<RevoteConfirmResult>(resolve => {
      const prev = get();
      if (prev.openTimer != null) {
        clearTimeout(prev.openTimer);
      }
      if (prev.resolver) {
        prev.resolver({ confirmed: false, dontShow: false });
      }

      const { kind, bonus } = normalizeRequest(input);
      // Bind resolver immediately so a newer request can cancel this one before open.
      set({
        open: false,
        kind: null,
        bonus: null,
        dontShow: false,
        resolver: resolve,
        openTimer: null,
      });

      // Defer past the originating iOS touch/click so Radix does not treat open as outside-dismiss.
      const openTimer = window.setTimeout(() => {
        if (get().resolver !== resolve) {
          return;
        }
        set({
          open: true,
          kind,
          bonus: bonus ?? null,
          dontShow: false,
          resolver: resolve,
          openTimer: null,
        });
      }, 50);

      set({ openTimer });
    }),
  setDontShow: dontShow => set({ dontShow }),
  confirm: () => {
    const state = get();
    resolveAndReset(state.resolver, { confirmed: true, dontShow: state.dontShow }, set, state.openTimer);
  },
  cancel: () => {
    const state = get();
    resolveAndReset(state.resolver, { confirmed: false, dontShow: state.dontShow }, set, state.openTimer);
  },
}));
